"use client";

import { c as _c } from "react/compiler-runtime";
import type { DropRateChangeRequest } from "@/entities/IDrop";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { addRecentQuickVoteAmount, normalizeQuickVoteAmount } from "@/hooks/memesQuickVote.helpers";
import { commonApiPost } from "@/services/api/common-api";
import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { useCallback, useRef, useState } from "react";
import type { TypeOptions } from "react-toastify";
const DEFAULT_DROP_RATE_CATEGORY = "Rep";
type SetToast = (options: {
  readonly message: string | ReactNode;
  readonly type: TypeOptions;
}) => void;
type PersistNumberArray = (updater: (current: number[]) => number[]) => void;
type QueuedRating = {
  readonly amount: number;
  readonly currentRating: number;
  readonly drop: ExtendedDrop;
  readonly maxRating: number;
  readonly type: "skip" | "vote";
};
type UseMemesQuickVoteSubmitOptions = {
  readonly requestAuth: () => Promise<{
    success: boolean;
  }>;
  readonly setToast: SetToast;
  readonly setAndPersistRecentAmounts: PersistNumberArray;
  readonly onRatingFailure: (drop: ExtendedDrop, amount: number, type: QueuedRating["type"]) => void;
  readonly onRatingQueued: (drop: ExtendedDrop, amount: number, type: QueuedRating["type"]) => void;
  readonly onRatingSuccess: (drop: ExtendedDrop, amount: number, nextRemainingPower: number, type: QueuedRating["type"]) => void;
};
type UseMemesQuickVoteSubmitResult = {
  readonly isVoting: boolean;
  readonly pendingDropIds: readonly string[];
  readonly submitSkip: (drop: ExtendedDrop) => Promise<boolean>;
  readonly submitVote: (drop: ExtendedDrop, amount: number | string) => Promise<boolean>;
};
type RefValue<T> = {
  current: T;
};
const getQueuedRatingAppliedAmount = ({
  nextRating,
  queuedRating
}: {
  readonly nextRating: number | undefined;
  readonly queuedRating: QueuedRating;
}): number => {
  if (queuedRating.type === "skip") {
    return 0;
  }
  if (typeof nextRating === "number") {
    return Math.max(0, nextRating - queuedRating.currentRating);
  }
  return queuedRating.amount;
};
const getQueuedRatingErrorMessage = ({
  error,
  type
}: {
  readonly error: unknown;
  readonly type: QueuedRating["type"];
}): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return type === "skip" ? "Quick vote couldn't skip that meme." : "Quick vote couldn't submit that vote.";
};
const flushMemesQuickVoteRatings = async ({
  isFlushingRef,
  onRatingFailure,
  onRatingSuccess,
  queuedDropIdsRef,
  ratingMutation,
  ratingQueueRef,
  requestAuth,
  setPendingDropIds,
  setPendingRatingCount,
  setToast
}: {
  readonly isFlushingRef: RefValue<boolean>;
  readonly onRatingFailure: UseMemesQuickVoteSubmitOptions["onRatingFailure"];
  readonly onRatingSuccess: UseMemesQuickVoteSubmitOptions["onRatingSuccess"];
  readonly queuedDropIdsRef: RefValue<Set<string>>;
  readonly ratingMutation: UseMutationResult<ApiDrop, Error, {
    readonly amount: number;
    readonly dropId: string;
  }>;
  readonly ratingQueueRef: RefValue<QueuedRating[]>;
  readonly requestAuth: UseMemesQuickVoteSubmitOptions["requestAuth"];
  readonly setPendingDropIds: Dispatch<SetStateAction<string[]>>;
  readonly setPendingRatingCount: Dispatch<SetStateAction<number>>;
  readonly setToast: SetToast;
}) => {
  if (isFlushingRef.current) {
    return;
  }
  isFlushingRef.current = true;
  try {
    while (ratingQueueRef.current.length > 0) {
      const queuedRating = ratingQueueRef.current[0];
      if (!queuedRating) {
        break;
      }
      try {
        const {
          success
        } = await requestAuth();
        if (!success) {
          throw new Error("Quick vote couldn't verify your session.");
        }
        const response = await ratingMutation.mutateAsync({
          amount: queuedRating.amount,
          dropId: queuedRating.drop.id
        });
        const appliedAmount = getQueuedRatingAppliedAmount({
          nextRating: response.context_profile_context?.rating,
          queuedRating
        });
        const nextRemainingPower = Math.max(0, queuedRating.maxRating - appliedAmount);
        onRatingSuccess(queuedRating.drop, queuedRating.amount, nextRemainingPower, queuedRating.type);
      } catch (error) {
        setToast({
          message: getQueuedRatingErrorMessage({
            error,
            type: queuedRating.type
          }),
          type: "error"
        });
        onRatingFailure(queuedRating.drop, queuedRating.amount, queuedRating.type);
      } finally {
        ratingQueueRef.current.shift();
        queuedDropIdsRef.current.delete(queuedRating.drop.id);
        setPendingDropIds(current => current.filter(dropId => dropId !== queuedRating.drop.id));
        setPendingRatingCount(ratingQueueRef.current.length);
      }
    }
  } finally {
    isFlushingRef.current = false;
  }
};
const queueMemesQuickVoteRating = ({
  amount,
  drop,
  flushQueuedRatings,
  onRatingQueued,
  queuedDropIdsRef,
  ratingQueueRef,
  setAndPersistRecentAmounts,
  setPendingDropIds,
  setPendingRatingCount,
  type
}: {
  readonly amount: number;
  readonly drop: ExtendedDrop;
  readonly flushQueuedRatings: () => void;
  readonly onRatingQueued: UseMemesQuickVoteSubmitOptions["onRatingQueued"];
  readonly queuedDropIdsRef: RefValue<Set<string>>;
  readonly ratingQueueRef: RefValue<QueuedRating[]>;
  readonly setAndPersistRecentAmounts: PersistNumberArray;
  readonly setPendingDropIds: Dispatch<SetStateAction<string[]>>;
  readonly setPendingRatingCount: Dispatch<SetStateAction<number>>;
  readonly type: QueuedRating["type"];
}): boolean => {
  if (queuedDropIdsRef.current.has(drop.id)) {
    return false;
  }
  ratingQueueRef.current.push({
    amount,
    currentRating: drop.context_profile_context?.rating ?? 0,
    drop,
    maxRating: drop.context_profile_context?.max_rating ?? 0,
    type
  });
  queuedDropIdsRef.current.add(drop.id);
  setPendingDropIds(current => current.includes(drop.id) ? current : [...current, drop.id]);
  setPendingRatingCount(ratingQueueRef.current.length);
  onRatingQueued(drop, amount, type);
  if (type === "vote") {
    setAndPersistRecentAmounts(current => addRecentQuickVoteAmount(current, amount));
  }
  flushQueuedRatings();
  return true;
};
export const useMemesQuickVoteSubmit = t0 => {
  const $ = _c(23);
  const {
    requestAuth,
    setToast,
    setAndPersistRecentAmounts,
    onRatingFailure,
    onRatingQueued,
    onRatingSuccess
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const ratingQueueRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const queuedDropIdsRef = useRef(t2);
  const isFlushingRef = useRef(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [pendingDropIds, setPendingDropIds] = useState(t3);
  const [pendingRatingCount, setPendingRatingCount] = useState(0);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      mutationFn: _temp
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const ratingMutation = useMutation(t4);
  let t5;
  if ($[4] !== onRatingFailure || $[5] !== onRatingSuccess || $[6] !== ratingMutation || $[7] !== requestAuth || $[8] !== setToast) {
    t5 = () => {
      flushMemesQuickVoteRatings({
        isFlushingRef,
        onRatingFailure,
        onRatingSuccess,
        queuedDropIdsRef,
        ratingMutation,
        ratingQueueRef,
        requestAuth,
        setPendingDropIds,
        setPendingRatingCount,
        setToast
      });
    };
    $[4] = onRatingFailure;
    $[5] = onRatingSuccess;
    $[6] = ratingMutation;
    $[7] = requestAuth;
    $[8] = setToast;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const flushQueuedRatings = t5;
  let t6;
  if ($[10] !== flushQueuedRatings || $[11] !== onRatingQueued || $[12] !== setAndPersistRecentAmounts) {
    t6 = t7 => {
      const {
        amount: amount_0,
        drop,
        type
      } = t7;
      return queueMemesQuickVoteRating({
        amount: amount_0,
        drop,
        flushQueuedRatings,
        onRatingQueued,
        queuedDropIdsRef,
        ratingQueueRef,
        setAndPersistRecentAmounts,
        setPendingDropIds,
        setPendingRatingCount,
        type
      });
    };
    $[10] = flushQueuedRatings;
    $[11] = onRatingQueued;
    $[12] = setAndPersistRecentAmounts;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const queueRating = t6;
  let t7;
  if ($[14] !== queueRating) {
    t7 = (drop_0, amount_1) => {
      const maxRating = drop_0.context_profile_context?.max_rating ?? 0;
      const normalizedAmount = normalizeQuickVoteAmount(amount_1, maxRating);
      if (normalizedAmount === null) {
        return Promise.resolve(false);
      }
      return Promise.resolve(queueRating({
        amount: normalizedAmount,
        drop: drop_0,
        type: "vote"
      }));
    };
    $[14] = queueRating;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const submitVote = t7;
  let t8;
  if ($[16] !== queueRating) {
    t8 = drop_1 => Promise.resolve(queueRating({
      amount: 0,
      drop: drop_1,
      type: "skip"
    }));
    $[16] = queueRating;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const submitSkip = t8;
  const t9 = pendingRatingCount > 0 || ratingMutation.isPending;
  let t10;
  if ($[18] !== pendingDropIds || $[19] !== submitSkip || $[20] !== submitVote || $[21] !== t9) {
    t10 = {
      isVoting: t9,
      pendingDropIds,
      submitSkip,
      submitVote
    };
    $[18] = pendingDropIds;
    $[19] = submitSkip;
    $[20] = submitVote;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
function _temp(t0) {
  const {
    dropId,
    amount
  } = t0;
  return commonApiPost({
    endpoint: `drops/${dropId}/ratings`,
    body: {
      rating: amount,
      category: DEFAULT_DROP_RATE_CATEGORY
    }
  });
}